import { t } from '@/shared/lib/i18n'
import { showErrorToast } from '@/shared/lib/toast'

/**
 * Delay before revoking the object URL so browsers can finish consuming large
 * blobs (lot attachments may be up to ~20MB).
 *
 * Residual (AC2): Rails `#download` already full-buffers via `send_data`, then this
 * helper reads `response.blob()` — up to ~20MB may be held twice briefly. Acceptable
 * while uploads stay capped; stream (ReadableStream → disk) only if concurrent
 * downloads create memory pressure.
 */
export const OBJECT_URL_REVOKE_DELAY_MS = 60_000

/** Abort reason when the download times out (toast gated by `didTimeout`, not this alone). */
export const DOWNLOAD_ABORT_REASON_TIMEOUT = 'timeout'

/** Abort reason when the dialog/section unmounts — must not toast. */
export const DOWNLOAD_ABORT_REASON_UNMOUNT = 'unmount'

export type DownloadLotAttachmentOptions = {
  /**
   * When false (e.g. dialog already closed), skip failure toasts and browser save.
   * Defaults to allowing outcomes.
   */
  isActive?: () => boolean
  /**
   * When true, an AbortError should surface the download-failed toast.
   * Prefer this over AbortSignal.reason — not all runtimes preserve abort reasons.
   */
  didTimeout?: () => boolean
  /**
   * Called after response headers validate as a real attachment — before `blob()`.
   * Use to restart the hung-request timeout for the body-read phase so slow ≤20MB
   * transfers are allowed (AC2) without leaving controls disabled forever on a stall.
   */
  onFileResponseAccepted?: () => void
}

const showDownloadFailedToast = (): void => {
  showErrorToast(t('instruments.lotsForm.attachments.downloadFailed'))
}

const showDownloadSessionExpiredToast = (): void => {
  showErrorToast(t('instruments.lotsForm.attachments.downloadSessionExpired'))
}

const triggerBrowserSave = (blob: Blob, filename: string): void => {
  const objectUrl = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = objectUrl
  anchor.download = filename
  anchor.rel = 'noopener'
  document.body.appendChild(anchor)
  anchor.click()
  document.body.removeChild(anchor)
  window.setTimeout(() => {
    URL.revokeObjectURL(objectUrl)
  }, OBJECT_URL_REVOKE_DELAY_MS)
}

/** First semicolon-delimited token of a header value, trimmed + lowercased. */
const headerTypeToken = (headerValue: string): string =>
  headerValue.split(';')[0]?.trim().toLowerCase() ?? ''

/** True for HTML media types only — not substring matches in parameters (e.g. `name=text/html`). */
const isHtmlContentType = (contentType: string): boolean => {
  const mediaType = headerTypeToken(contentType)
  return mediaType === 'text/html' || mediaType === 'application/xhtml+xml'
}

/** Soft API/error JSON payloads must not be saved as the attachment when CD is stripped. */
const isJsonContentType = (contentType: string): boolean => {
  const mediaType = headerTypeToken(contentType)
  return mediaType === 'application/json' || mediaType === 'application/problem+json'
}

/** True when Content-Disposition's type token is exactly `attachment` (RFC 6266). */
const hasAttachmentContentDisposition = (contentDisposition: string): boolean =>
  headerTypeToken(contentDisposition) === 'attachment'

/**
 * Unambiguous file media types when Content-Disposition was stripped by a proxy.
 * Must stay in sync with `StorageAttachmentMimeTypes::ALLOWED_MIME_TYPES` except
 * `text/plain` — soft gateway/error bodies often use `text/plain` (and JSON/HTML)
 * without CD. Entries are lowercase because `headerTypeToken` lowercases.
 * With CD type `attachment`, `text/plain` remains a valid lot-attachment MIME.
 */
export const MISSING_DISPOSITION_SAFE_MEDIA_TYPES = new Set([
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/tiff',
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/rtf',
  'text/csv',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-excel.sheet.macroenabled.12',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'application/x-cfb',
  'application/zip',
  'application/x-zip-compressed',
])

const isMissingDispositionSafeContentType = (contentType: string): boolean =>
  MISSING_DISPOSITION_SAFE_MEDIA_TYPES.has(headerTypeToken(contentType))

/**
 * Session/auth UX: OK response with an HTML body (login page after redirect follow, or soft HTML).
 * Do not treat non-OK responses (including redirected 404/5xx) as session expiry —
 * those are storage/server failures and must use downloadFailed.
 */
const isSessionOrAuthFailureResponse = (response: Response): boolean => {
  if (!response.ok) return false
  const contentType = response.headers.get('Content-Type') ?? ''
  return isHtmlContentType(contentType)
}

/**
 * Reject HTML error pages and non-file bodies so we never save them as the attachment.
 * Do not reject solely because `response.redirected` — same-origin redirects to a real
 * file response are still valid.
 *
 * Contract with `Web::Instruments::LotAttachmentsController#download` (`send_data`
 * disposition: 'attachment'): when Content-Disposition is present, its type token
 * before the first `;` must be exactly `attachment` (any allowed lot MIME, including
 * `text/plain`). When a proxy strips CD, accept only unambiguous safe Content-Types
 * (not empty / JSON / HTML / soft `text/plain` error bodies).
 */
const isSuccessfulFileResponse = (response: Response): boolean => {
  if (!response.ok) return false

  const contentType = response.headers.get('Content-Type') ?? ''
  if (isHtmlContentType(contentType)) return false

  const contentDisposition = response.headers.get('Content-Disposition') ?? ''
  if (!contentDisposition.trim()) {
    // Proxy stripped CD — require unambiguous file CT (reject soft text/plain / JSON / empty).
    if (!contentType.trim()) return false
    if (isJsonContentType(contentType)) return false
    return isMissingDispositionSafeContentType(contentType)
  }

  return hasAttachmentContentDisposition(contentDisposition)
}

/**
 * Serializer `download_href` shape only — not every same-origin URL.
 * `/instruments/:id/catalog_lots/:lot_id/attachments/:id/download`
 */
const LOT_ATTACHMENT_DOWNLOAD_PATH_PATTERN =
  /^\/instruments\/\d+\/catalog_lots\/\d+\/attachments\/\d+\/download\/?$/

/** Pathname for app-relative or same-origin http(s) hrefs; null when untrusted. */
const trustedDownloadPathname = (href: string): string | null => {
  // Protocol-relative URLs resolve against the page origin via `new URL` — reject first.
  if (href.startsWith('//')) return null

  try {
    const parsedUrl = new URL(href, window.location.origin)
    // Reject blob:/data:/javascript: even when origin matches the page.
    if (parsedUrl.protocol !== 'http:' && parsedUrl.protocol !== 'https:') return null
    if (parsedUrl.origin !== window.location.origin) return null
    return parsedUrl.pathname
  } catch {
    return null
  }
}

/** Lot attachment download path on this origin — never credentialed cross-origin / opaque-scheme fetch. */
export const isTrustedDownloadHref = (href: string): boolean => {
  const trimmedHref = href.trim()
  if (!trimmedHref) return false

  const pathname = trustedDownloadPathname(trimmedHref)
  if (!pathname) return false

  return LOT_ATTACHMENT_DOWNLOAD_PATH_PATTERN.test(pathname)
}

export const resolveAttachmentFilename = (attachmentName: string | null | undefined): string =>
  attachmentName?.trim() || t('instruments.lotsForm.attachments.fallbackFilename')

const isAbortError = (error: unknown): boolean =>
  (error instanceof DOMException && error.name === 'AbortError') ||
  (error instanceof Error && error.name === 'AbortError')

const isDownloadContextActive = (isActive?: () => boolean): boolean => isActive?.() ?? true

/** Free the connection when we will not read a failed/non-file response body. */
const discardResponseBody = (response: Response): void => {
  try {
    const cancelResult = response.body?.cancel()
    // `cancel()` returns a Promise — swallow async rejection (locked/closed streams).
    void Promise.resolve(cancelResult).catch(() => undefined)
  } catch {
    // Body may already be locked or unavailable (e.g. head responses).
  }
}

/** Fetch download so 404/network failures can toast instead of navigating away. */
export const downloadLotAttachment = async (
  downloadHref: string | null | undefined,
  attachmentName: string | null | undefined,
  signal?: AbortSignal,
  options?: DownloadLotAttachmentOptions,
): Promise<void> => {
  const notifyFailure = (sessionOrAuth = false): void => {
    if (!isDownloadContextActive(options?.isActive)) return
    // Aborted fetches must stay silent unless this was an intentional timeout.
    if (signal?.aborted && !options?.didTimeout?.()) return
    if (sessionOrAuth) {
      showDownloadSessionExpiredToast()
      return
    }
    showDownloadFailedToast()
  }

  const trimmedDownloadHref = downloadHref?.trim() ?? ''
  if (!trimmedDownloadHref) {
    notifyFailure()
    return
  }

  if (!isTrustedDownloadHref(trimmedDownloadHref)) {
    notifyFailure()
    return
  }

  try {
    const response = await fetch(trimmedDownloadHref, {
      credentials: 'same-origin',
      // Avoid sticky browser 404s after a prior failure (success path already sends no-store).
      cache: 'no-store',
      signal,
    })
    if (!isSuccessfulFileResponse(response)) {
      discardResponseBody(response)
      notifyFailure(isSessionOrAuthFailureResponse(response))
      return
    }

    // Unmount/cancel after headers: do not restart timers, read body, toast, or save.
    // Timeout aborts continue so a late body can still save (AC2) or toast on hang.
    if (signal?.aborted && !options?.didTimeout?.()) {
      discardResponseBody(response)
      return
    }

    // Headers prove a file is coming — let the caller replace the hung-TTFB timer
    // with a body-read window so a stalled transfer cannot disable controls forever.
    options?.onFileResponseAccepted?.()

    const blob = await response.blob()
    if (blob.size === 0) {
      notifyFailure()
      return
    }

    if (!isDownloadContextActive(options?.isActive)) return
    // Timeout after blob is in memory: still save (AC2). Unmount/cancel stays silent.
    if (signal?.aborted && !options?.didTimeout?.()) return

    triggerBrowserSave(blob, resolveAttachmentFilename(attachmentName))
  } catch (error) {
    if (isAbortError(error) || signal?.aborted) {
      if (options?.didTimeout?.()) {
        notifyFailure()
      }
      return
    }

    notifyFailure()
  }
}
